import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import {
  Card,
  Image,
  Icon,
  Container,
  Label,
  Button,
  Popup,
  Checkbox,
  Divider
} from 'semantic-ui-react';

import modes from './constants/modes';

import {
  makePut,
  makeGetWithParam
} from './../../../../src/services/ServiceManager';

import {
  getFileType,
  getFilename,
  getFilenameWithoutExtension,
  getUploadedBy,
  getAssetUrl
} from './Utils/asset.utils';

import AssetTagSelection from './AssetTagSelection';

import errors from './constants/errors';

class AssetPreview extends PureComponent {
  constructor(props) {
    super(props);

    const { asset } = props;
    const { type, tags, archived } = asset;
    const assetType =
      type && type.toLowerCase().indexOf('image') !== -1 ? 'image' : 'video';

    const assetUrl = getAssetUrl(asset);

    const selectedTagOptions = {};
    if (tags && tags.length > 0) {
      for (const tag of tags) {
        const { category } = tag;

        const tagOption = {
          key: tag.id,

          text: tag.value,
          value: tag.id,
          category: tag.category
        };

        if (selectedTagOptions[category]) {
          selectedTagOptions[category].push(tagOption);
        } else {
          selectedTagOptions[category] = [tagOption];
        }
      }
    }

    this.state = {
      ...props,
      assetType,
      openEditPopup: false,
      selected: false,
      assetUrl,
      selectedTagOptions,
      assetNameLengthLimit: 25,
      uploadedByLengthLimit: 15,
      loadingOptions: false,
      isArchived: archived === 'true'
    };
  }

  componentWillReceiveProps(nextProps) {
    const { selected, asset } = this.state;
    const { selected: selectedInProps, asset: assetInProps } = nextProps;

    if (selected !== selectedInProps) {
      this.setState((prevState) => ({
        ...prevState,
        selected: selectedInProps
      }));
    } else if (!_.isEqual(asset, assetInProps)) {
      // when asset tags are update, need to re-render
      this.setState((prevState) => ({
        ...prevState,
        asset: assetInProps
      }));
    }
  }

  onEditButtonClick = () => {
    // When edit tag button is clicked, we need to pre-fill tags for the selected asset.
    // use updateMultipleTagOptions() defined in AssetGallery.
    // First check if the tag options for each selected tag keys are loaded.
    // Select the keys that aren't loaded, and load them using the function.
    const { asset, user } = this.props;
    const { tags } = asset || {};

    const { groups, email } = user;
    const applyGuestFilter = ['NU'].includes(groups[0]);

    const assetCategories = tags ? tags.map((tag) => tag.category) : [];

    // filter out the categories that are already loaded.
    const { tagOptions } = this.props;
    const loadedCategories = Object.keys(tagOptions);

    const categoriesToLoad = assetCategories.filter(
      (category) => loadedCategories.indexOf(category) === -1
    );

    if (categoriesToLoad.length === 0) {
      this.setState((prevState) => ({
        ...prevState,
        loadingOptions: false,
        openEditPopup: true
      }));

      return;
    }

    this.setState(
      {
        ...this.state,
        loadingOptions: true,
        openEditPopup: true
      },
      () => {
        makeGetWithParam(
          'service/tags/categories',
          null,
          `?${this.createCategoryQueryString(categoriesToLoad)}${
            applyGuestFilter ? `&created_by=${encodeURIComponent(email)}` : ''
          }`
        ).then((result) => {
          const { tags: returnedTags } = result || {};

          if (!returnedTags || Object.keys(returnedTags).length === 0) {
            this.setState((prevState) => ({
              ...prevState,
              loadingOptions: false
            }));

            return;
          }

          this.setState(
            {
              ...this.state,
              loadingOptions: false
            },
            () => {
              const tmpTagOptions = {};

              for (const key of Object.keys(returnedTags)) {
                const tagsUnderCategory = returnedTags[key];

                tmpTagOptions[key] = tagsUnderCategory.map((tag) => ({
                  key: tag.id,
                  text: tag.value,
                  value: tag.id,
                  category: key
                }));
              }

              const { updateMultipleTagOptions } = this.props;

              if (updateMultipleTagOptions)
                updateMultipleTagOptions(tmpTagOptions);
            }
          );
        });
      }
    );
  };

  createCategoryQueryString = (categories) => {
    return categories
      .map((category, index) => `categories[${index}]=${category}`)
      .join('&');
  };

  onEditPopupClose = () => {
    // on popup close, if selectedTagOptions are empty, it means reset button has been clicked.
    // fill it again so that if the user click on the edit button next time, the original tags are displayed.
    const { selectedTagOptions } = this.state;

    if (Object.keys(selectedTagOptions)) {
      const newSelectedTagOptions = {};
      const { asset } = this.props;
      const { tags } = asset || {};

      if (!tags) return;

      for (const tag of tags) {
        const { category } = tag;

        const tagOption = {
          key: tag.id,

          text: tag.value,
          value: tag.id,
          category: tag.category
        };

        if (newSelectedTagOptions[category]) {
          newSelectedTagOptions[category].push(tagOption);
        } else {
          newSelectedTagOptions[category] = [tagOption];
        }
      }

      this.setState((prevState) => ({
        ...prevState,
        selectedTagOptions: newSelectedTagOptions,
        openEditPopup: false
      }));

      return;
    }

    this.setState((prevState) => ({
      ...prevState,
      openEditPopup: false
    }));
  };

  onAssetClick = () => {
    const { onAssetClick: onAssetClickInProps, mode, asset } = this.props;

    // In EDITTING mode, the checkbox has to be checked in order to select an asset.
    if (mode !== modes.ASSET_GALLERY.EDITTING) onAssetClickInProps(asset, true);
  };

  onCheckboxClick = (event, data) => {
    const { checked } = data;
    const { onAssetClick: onAssetClickInProps } = this.props;
    const { asset } = this.props;

    // In EDITTING mode, the checkbox has to be checked in order to select an asset.
    onAssetClickInProps(asset, checked);
  };

  resetSelectTagOptions = () => {
    this.setState((prevState) => ({
      ...prevState,
      selectedTagOptions: {}
    }));
  };

  updateSelectedTagOptions = (category, options) => {
    this.setState((prevState) => ({
      ...prevState,
      selectedTagOptions: {
        ...prevState.selectedTagOptions,
        [category]: options
      }
    }));
  };

  updateAssetTags = (callback) => {
    const { asset } = this.props;
    const { selectedTagOptions } = this.state;
    const { id: assetId } = asset;

    // prevent app crashing...
    // check only if selectedTagOptions is valid.. The asset must exists otherwise we can proceed if the asset is empty.
    if (!selectedTagOptions && callback) {
      callback({
        error: {
          type: 'UPDATE_TAG_FAIL',
          message: 'There was problem updating tags. Please try again.'
        }
      });

      return;
    }

    if (
      selectedTagOptions &&
      Object.keys(selectedTagOptions).length === 0 &&
      callback
    ) {
      callback({
        type: errors.ASSET_TAG_SELECTION.NO_TAG_SELECTED,
        message: 'No tags are selected. Please select at least one tag.'
      });

      return;
    }

    const tagIds = Object.keys(selectedTagOptions)
      .map((key) => {
        const options = selectedTagOptions[key];

        return options;
      })
      .flat()
      .map((option) => option.key);

    makePut(
      'service/tags',
      {
        assetId,
        tagIds
      },
      null
    ).then((result) => {
      const { statusCode, tags } = result;

      if (statusCode === 200) {
        this.setState(
          {
            ...this.state,

            asset: {
              ...this.state.asset,
              tags
            },
            openEditPopup: false
          },
          () => {
            const { updateAssetTags: updateAssetTagsInProps } = this.props; // this function gets from AssetGallery

            if (updateAssetTagsInProps) updateAssetTagsInProps([assetId], tags);
          }
        );
      } else {
        callback({
          type: 'UPDATE_TAG_FAIL',
          message: 'There was an error. Please try again.'
        });
      }
    });
  };

  toggleArchivedStatusForAsset = (assetId, assetFilename) => {
    const { toggleArchivedStatusForAsset } = this.props;

    this.setState(
      (prevState) => ({ isArchived: !prevState.isArchived }),
      () => {
        if (toggleArchivedStatusForAsset) {
          toggleArchivedStatusForAsset(
            assetId,
            assetFilename,
            this.state.isArchived
          );
        }
      }
    );
  };

  render() {
    const {
      openEditPopup,
      selected,
      assetType,
      assetUrl,
      selectedTagOptions,
      asset,
      assetNameLengthLimit,
      uploadedByLengthLimit,
      loadingOptions,
      isArchived
    } = this.state;

    const {
      tagOptions,
      updateTagOptions,
      mode,
      inArchiveMode,
      user
    } = this.props;
    const {
      tags,
      filename,
      created_at: createdAt,
      uploaded_by: uploadedBy,
      id: assetId,
      filename: assetFilename
    } = asset;

    const hasLongAssetName = filename && filename.length > assetNameLengthLimit;
    const hasLongUploadedBy =
      uploadedBy && uploadedBy.length > uploadedByLengthLimit;

    const createdDateString = createdAt ? createdAt.fromNow() : '-'; // created_at is moment object

    const assetNameCopyButton = (
      <CopyToClipboard text={filename}>
        <Button
          style={{ marginLeft: '1em' }}
          basic
          size="mini"
          icon="copy outline"
        />
      </CopyToClipboard>
    );

    return (
      <Card
        style={
          selected
            ? { border: '2px solid gray', backgroundColor: '#F0F0F0' }
            : {}
        }
        onClick={this.onAssetClick}
      >
        {assetType === 'image' && (
          <Image src={assetUrl} style={{ width: '100%', height: '15em' }} />
        )}

        {assetType === 'video' && (
          <video
            src={assetUrl}
            type="video/mp4"
            controls
            muted
            style={{ width: '100%', height: '15em' }}
          >
            <track kind="captions" />
          </video>
        )}
        <Card.Content>
          {hasLongAssetName ? (
            <Popup
              content={getFilenameWithoutExtension(filename)}
              trigger={
                <Card.Header>
                  <span>
                    {getFilename(asset, assetNameLengthLimit)}
                    {assetNameCopyButton}
                  </span>
                </Card.Header>
              }
            />
          ) : (
            <Card.Header>
              <span>
                {getFilename(asset, assetNameLengthLimit)}
                {assetNameCopyButton}
              </span>
            </Card.Header>
          )}

          <Card.Meta>{`${getFileType(asset)}`}</Card.Meta>
          <Card.Description>
            <Container>
              {tags.map((tag) => {
                const { value, category } = tag;

                return (
                  <Label
                    key={`asset-preview-tag-${value}`}
                    style={{ marginBottom: '0.5em' }}
                    size="small"
                  >
                    {category}:<Label.Detail>{value}</Label.Detail>
                  </Label>
                );
              })}
            </Container>
          </Card.Description>
        </Card.Content>
        <Card.Content extra style={{ height: '3.6em', position: 'relative' }}>
          {mode === modes.ASSET_GALLERY.NORMAL && (
            <Container style={{ height: '100%' }}>
              <span
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                {hasLongUploadedBy ? (
                  <span>
                    {`${createdDateString} by `}
                    <Popup
                      content={uploadedBy}
                      trigger={
                        <span style={{ color: '#4183c4' }}>
                          {getUploadedBy(asset, uploadedByLengthLimit)}
                        </span>
                      }
                    />
                  </span>
                ) : (
                  <span>
                    {`${createdDateString} by `}
                    <span style={{ color: '#4183c4' }}>
                      {getUploadedBy(asset, uploadedByLengthLimit)}
                    </span>
                  </span>
                )}
              </span>
              <Button
                basic
                icon
                floated="right"
                size="mini"
                onClick={this.onRemoveAssetButtonClick}
              >
                <Popup
                  trigger={
                    <Icon name="edit" onClick={this.onEditButtonClick} />
                  }
                  onClose={this.onEditPopupClose}
                  position="top center"
                  open={openEditPopup}
                  on="click"
                >
                  <AssetTagSelection
                    allowAdditions
                    ableToUpdateTags
                    tagOptions={tagOptions}
                    updateTagOptions={updateTagOptions}
                    loadingOptions={loadingOptions}
                    updateSelectedTagOptions={this.updateSelectedTagOptions}
                    updateAssetTags={this.updateAssetTags}
                    selectedTagOptions={selectedTagOptions}
                    resetSelectTagOptions={this.resetSelectTagOptions}
                    inArchiveMode={inArchiveMode}
                    style={{
                      width: '30em'
                    }}
                    user={user}
                  />
                  <Divider hidden />
                  <Checkbox
                    label="Archived"
                    toggle
                    onChange={() =>
                      this.toggleArchivedStatusForAsset(assetId, assetFilename)
                    }
                    checked={isArchived}
                  />
                </Popup>
              </Button>
            </Container>
          )}

          {mode === modes.ASSET_GALLERY.EDITTING && (
            <Checkbox
              style={{ float: 'right', transform: 'translateY(25%)' }}
              onClick={this.onCheckboxClick}
              checked={selected}
            />
          )}
        </Card.Content>
      </Card>
    );
  }
}

AssetPreview.defaultProps = {
  selected: false
};

/** eslint  */
AssetPreview.propTypes = {
  asset: PropTypes.shape({
    id: PropTypes.number.isRequired,
    facebook_asset_id: PropTypes.number.isRequiredOrNull,
    filename: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    video_thumbnail: PropTypes.string.isRequiredOrNull,
    tags: PropTypes.arrayOf(PropTypes.object).isRequired,
    uploaded_by: PropTypes.string.isRequired,
    created_at: PropTypes.object.isRequired // moment instance
  }).isRequired,
  tagOptions: PropTypes.object.isRequired,
  updateTagOptions: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired, // NORMAL, EDITTING,
  onAssetClick: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  updateAssetTags: PropTypes.func.isRequired, // this function gets from AssetGallery
  updateMultipleTagOptions: PropTypes.func.isRequired
};

export default AssetPreview;



// WEBPACK FOOTER //
// ./src/components/common/AssetGalleryV2/AssetPreview.jsx